import React from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  useTheme,
  alpha
} from "@mui/material";

function Navigation() {
  const theme = useTheme();
  const location = useLocation();

  const navLinks = [
    { label: "Studio", path: "/studio" },
    { label: "About", path: "/about" },
    { label: "Resume", path: "/resume" },
    { label: "Chat", path: "/chat" },
    { label: "Hire", path: "/hire" },
  ];

  return (
    <AppBar 
      position="static" 
      elevation={0}
      sx={{ 
        // Opaque, no backdrop blur: the AppBar is position="static", so nothing
        // ever passes behind it — the blur was a no-op compositing layer.
        backgroundColor: theme.palette.background.default,
        borderBottom: theme.palette.mode === 'dark'
          ? `1px solid ${alpha(theme.palette.common.white, 0.1)}`
          : `1px solid ${alpha(theme.palette.common.black, 0.1)}`
      }}
    >
      {/* Skip link: the first thing in the tab order on every page, visible only
          while focused. Without it a keyboard or screen-reader user traverses all
          five nav items before reaching content, on every navigation. */}
      <Box
        component="a"
        href="#main"
        sx={{
          position: "absolute",
          left: 8,
          top: 8,
          zIndex: (t) => t.zIndex.appBar + 1,
          px: 2,
          py: 1.25,
          borderRadius: 1,
          fontSize: "0.875rem",
          fontWeight: 600,
          textDecoration: "none",
          color: "text.primary",
          backgroundColor: "background.paper",
          border: `1px solid ${theme.palette.divider}`,
          // Off-screen rather than display:none, so it stays focusable.
          transform: "translateY(-200%)",
          transition: "transform 0.2s ease",
          "&:focus-visible": {
            transform: "translateY(0)",
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
        }}
      >
        Skip to content
      </Box>

      {/* Five nav items + the wordmark overflow a 390px viewport at the default
          gutters — "Hire" landed off-screen and the page scrolled sideways. The
          xs values below buy that width back from padding and type size.
          The 44px tap-target floor is not part of the trade — and one row could
          not honour it: at 390px the wordmark plus five labels left ~43px per
          link with ZERO gap between them (Chat 41x44 and Hire 38x44, touching at
          x=344). Rather than shrink the targets further or collapse the labels
          into a low-scent icon, xs stacks the bar into two rows so every link
          clears 44x44 with real spacing and keeps its word. */}
      <Toolbar
        sx={{
          px: { xs: 1, sm: 2 },
          gap: 1,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          py: { xs: 1, sm: 0 },
        }}
      >
        <Typography
          variant="h6"
          component={RouterLink}
          to="/"
          sx={{
            // Was flexGrow: 1, which stretched the anchor box to 1039px on
            // desktop — clicking empty header whitespace navigated home. The
            // link is now only as wide as its text; the spacer below does the
            // pushing.
            alignSelf: { xs: "flex-start", sm: "center" },
            flexShrink: 0,
            display: "inline-flex",
            alignItems: "center",
            minHeight: 44, // tap-target floor, same as the nav links
            whiteSpace: "nowrap",
            fontSize: { xs: "0.95rem", sm: "1.25rem" },
            textDecoration: "none",
            color: "text.primary",
            fontWeight: 600,
            letterSpacing: "0px",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            '&:hover': {
              color: 'primary.main',
              letterSpacing: "1px",
            },
            '&:focus-visible': {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
              borderRadius: 2,
            },
          }}
        >
          David Kooi
        </Typography>

        {/* Spacer — takes over the job flexGrow was doing on the wordmark. */}
        <Box sx={{ display: { xs: "none", sm: "block" }, flexGrow: 1 }} />

        <Box
          component="nav"
          aria-label="Primary"
          sx={{
            display: "flex",
            gap: { xs: 1, sm: 0.5 },
            minWidth: 0,
            width: { xs: "100%", sm: "auto" },
          }}
        >
          {navLinks.map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <Box
                key={link.path}
                component={RouterLink}
                to={link.path}
                aria-current={isActive ? "page" : undefined}
                sx={{
                  position: "relative",
                  textDecoration: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 44, // tap-target floor
                  minWidth: 44,  // ...and the other axis: "Hire" was 38px wide
                  flex: { xs: 1, sm: "0 0 auto" },
                  px: { xs: 0.5, sm: 2 },
                  borderRadius: 1,
                  transition: "background-color 0.2s ease",
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  },
                  '&:focus-visible': {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: 2,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? 'primary.main' : 'text.primary',
                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                    whiteSpace: "nowrap",
                    transition: "color 0.2s ease",
                    '&:hover': {
                      color: 'primary.main',
                    }
                  }}
                >
                  {link.label}
                </Typography>
                {/* Animated underline */}
                {isActive && (
                  <motion.div
                    layoutId="nav-underline"
                    style={{
                      position: "absolute",
                      bottom: 4,
                      left: 8,
                      right: 8,
                      height: 2,
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: 1,
                    }}
                    transition={{
                      type: "spring",
                      stiffness: 500,
                      damping: 30,
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Toolbar>
    </AppBar>
  );
}

export default Navigation;
